/**
 * DASHBOARD-RPS-GOLLAC-OVERVIEW.WIDGET
 * Presenat
 */

import { useEffect, useState } from "react"
import { store } from "@/index"
import { fetchRepartition } from "@/redux/_archive.actions"
import Card from "@/components/card"
import DashboardRepartitionBarsWidget from "@/widgets/dashboard-repartition-bars.widget"
import { STATUS_LOADED, STATUS_LOADING } from "@/redux/_status.types"
import { t } from "i18next"
import { Trans } from "react-i18next"
import { faInfoCircle, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useAppSelector } from "@/core/root-reducer"
import HelpModal from "@/components/modal-help"
import Link from "@/components/link"
import { flatten, uniq } from "lodash"
import { getRepartitionCeils } from "@/utils/get-repartition-ceils.utils"

interface Props {
  isReport?: boolean
  onLoad?: (hasData: boolean) => void
}

export default function DashboardRpsGollacOverwiewWidget(props: Props) {
  const { isReport, onLoad } = props

  // selectors
  const _session = useAppSelector((state) => state._session)
  const filter = useAppSelector((state) => state.filter)
  const survey = useAppSelector((state) => state.survey)
  const topic = useAppSelector((state) => state.topic)

  const ceilMin = getRepartitionCeils(_session, true, false)[0]

  const responseCountList = uniq(
    flatten(topic.list.map((x) => x.Questions.filter((x) => x.type === "scale").map((x) => x.responseCount)))
  )

  let responseCountListLabels: string[][] = []
  if (responseCountList.includes(5)) {
    responseCountListLabels = [
      [t("question_likert_1"), "0"],
      [t("question_likert_2"), "2.5"],
      [t("question_likert_3"), "5"],
      [t("question_likert_4"), "7.5"],
      [t("question_likert_5"), "10"]
    ]
  } else {
    responseCountListLabels = [
      [t("question_likert_1"), "0"],
      [t("question_likert_2"), "2.5"],
      [t("question_likert_4"), "7.5"],
      [t("question_likert_5"), "10"]
    ]
  }
  if (responseCountList.includes(2)) {
    responseCountListLabels = responseCountListLabels.concat([
      [t("utils_yes"), "10"],
      [t("utils_no"), "0"]
    ])
  }

  // states
  const [values, setValues] = useState<number[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showHelp, setShowHelp] = useState(false)

  // getData
  useEffect(() => {
    async function load() {
      setIsLoading(true)

      const response: any = await store.dispatch(
        fetchRepartition(filter.dashboard, false, null, null, undefined, undefined, isReport, true)
      )

      if (!response.error) {
        setValues(response.values)
        onLoad?.(response.values.length > 0)
      } else {
        setValues([])
        onLoad?.(false)
      }

      setIsLoading(false)
    }

    load()
  }, [filter.dashboard])

  // render
  return (
    <>
      <Card title={t("dashboard_overview")} status={isLoading ? STATUS_LOADING : STATUS_LOADED}>
        <div className="absolute top-[14px] right-6 text-sm">
          <Link icon={faInfoCircle} onClick={() => setShowHelp(true)}>
            {t("utils_help")}
          </Link>
        </div>
        <div className="flex" style={{ gap: 35 }}>
          <div className="flex flex-col gap-3 justify-center">
            <p className="grey-t m-0 leading-relaxed">{t("gollac_overview_paragraph_1")}</p>

            <p className="grey-t m-0 leading-relaxed">{t("gollac_overview_paragraph_2", { ceilMin })}</p>

            <div className="grid grid-cols-[18px_1fr] gap-x-2 items-start">
              <FontAwesomeIcon icon={faTriangleExclamation} className="orange-t text-sm mt-[3px]" />
              <div className="grey-t leading-relaxed">{t("gollac_overview_warning")}</div>
            </div>
          </div>

          {/* repartition */}
          <div className="-mt-8 flex-1 min-w-[480px]">
            <DashboardRepartitionBarsWidget values={values} showSubtext={false} showCeils displaySatisfaction />
          </div>
        </div>
      </Card>
      {showHelp && (
        <HelpModal title={t("survey_repartition")} onClose={() => setShowHelp(false)}>
          <div className="max-w-xl mx-auto bg-white">
            <h2 className="text-primary font-bold">{t("utils_help_repartition_h1_1")}</h2>
            <p>{t("utils_help_repartition_p_1")}</p>
            <p>{t("utils_help_repartition_p_2")}</p>

            <h3 className="mt-6 font-semibold text-gray-700">{t("utils_help_repartition_h2_1")}</h3>
            <table className="w-full border border-gray-300 mt-4">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-2 text-left">{t("question_response")}</th>
                  <th className="border border-gray-300 px-4 py-2 text-left">{t("utils_help_repartition_th_1")}</th>
                </tr>
              </thead>
              <tbody>
                {responseCountListLabels.map(([label, points]) => (
                  <tr key={label} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-4 py-2">{label}</td>
                    <td className="border border-gray-300 px-4 py-2">{points}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h2 className="text-primary font-bold">{t("utils_help_cs_h2_2")}</h2>
            <p>{t("utils_help_cs_p_2")}</p>
            <h3 className="mt-6 font-semibold text-gray-700">{t("utils_help_cs_h3_1")}</h3>
            <ul>
              <li>{<Trans>{t("utils_help_cs_li_6")}</Trans>}</li>
              <li>{<Trans>{t("utils_help_cs_li_7")}</Trans>}</li>
              <li>{<Trans>{t("utils_help_cs_li_8")}</Trans>}</li>
            </ul>

            <h2 className="text-primary font-bold mt-10">{t("utils_help_cs_h2_3")}</h2>
            <p>{t("utils_help_cs_p_3")}</p>
            <h3 className="mt-6 font-semibold text-gray-700">{t("utils_help_cs_h3_1")}</h3>
            <ul>
              <li>{<Trans>{t("utils_help_cs_li_9")}</Trans>}</li>
              <li>{<Trans>{t("utils_help_cs_li_10")}</Trans>}</li>
              <li>{<Trans>{t("utils_help_cs_li_11")}</Trans>}</li>
              <li>{<Trans>{t("utils_help_cs_li_12")}</Trans>}</li>
              <li>{<Trans>{t("utils_help_cs_li_13")}</Trans>}</li>
              <li>{<Trans>{t("utils_help_cs_li_14")}</Trans>}</li>
            </ul>

            <div className="bg-yellow-100 border-l-4 border-yellow-400 text-yellow-800 p-4 mt-6 rounded-md">
              <p className="font-medium">{t("utils_help_cs_p_4")}</p>
              <p>{t("utils_help_cs_p_5")}</p>
            </div>

            {survey.active.template.options.isGollac && (
              <div>
                <h2>{t("gollac_overview_help_title")}</h2>
                <p>{t("gollac_overview_help_intro")}</p>
                <h3>{t("gollac_overview_help_h3_expo")}</h3>
                <p>{t("gollac_overview_help_p_expo")}</p>
                <h3>{t("gollac_overview_help_h3_secure")}</h3>
                <p>{t("gollac_overview_help_p_secure")}</p>
                <p>{t("gollac_overview_help_p_standard")}</p>
                <p className="italic text-gray-500 text-sm mt-2">
                  {t("gollac_overview_ceils_note")}
                </p>
              </div>
            )}
          </div>
        </HelpModal>
      )}
    </>
  )
}
